import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
      };

      const response = await axios.put('/api/users/profile', updateData);

      if (response.data) {
        updateUser(response.data);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      }
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Failed to update profile',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (formData.new_password !== formData.confirm_password) {
      setMessage({ type: 'danger', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (formData.new_password.length < 6) {
      setMessage({ type: 'danger', text: 'New password must be at least 6 characters long' });
      setLoading(false);
      return;
    }

    try {
      await axios.put('/api/auth/change-password', {
        currentPassword: formData.current_password,
        newPassword: formData.new_password,
      });

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setFormData((prev) => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      setShowPasswordFields(false);
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Failed to update password',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Row className="mt-3">
        <Col md={8} lg={6} className="mx-auto">
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <i className="fas fa-user me-2"></i>
                User Profile
              </h4>
            </Card.Header>
            <Card.Body>
              {message.text && (
                <Alert variant={message.type} className="mb-3">
                  {message.text}
                </Alert>
              )}

              {/* Profile Information Form */}
              <Form onSubmit={handleProfileUpdate}>
                <h5 className="mb-3 text-primary">
                  <i className="fas fa-info-circle me-2"></i>
                  Profile Information
                </h5>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        placeholder="Enter first name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        placeholder="Enter last name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Button type="submit" variant="primary" disabled={loading} className="me-2">
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Update Profile
                    </>
                  )}
                </Button>
              </Form>

              <hr className="my-4" />

              {/* Password Change Section */}
              <div>
                <h5 className="mb-3 text-primary">
                  <i className="fas fa-lock me-2"></i>
                  Change Password
                </h5>

                {!showPasswordFields ? (
                  <Button variant="outline-primary" onClick={() => setShowPasswordFields(true)}>
                    <i className="fas fa-key me-2"></i>
                    Change Password
                  </Button>
                ) : (
                  <Form onSubmit={handlePasswordChange}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="current_password"
                        value={formData.current_password}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter current password"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="new_password"
                        value={formData.new_password}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter new password (min 6 characters)"
                        minLength={6}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleInputChange}
                        required
                        placeholder="Confirm new password"
                        minLength={6}
                      />
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button type="submit" variant="success" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-2"></i>
                            Update Password
                          </>
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => {
                          setShowPasswordFields(false);
                          setFormData((prev) => ({
                            ...prev,
                            current_password: '',
                            new_password: '',
                            confirm_password: '',
                          }));
                        }}
                      >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </div>

              <hr className="my-4" />

              {/* Account Information */}
              <div>
                <h5 className="mb-3 text-primary">
                  <i className="fas fa-info me-2"></i>
                  Account Information
                </h5>

                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Account Created:</strong>
                    </p>
                    <p className="text-muted">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Last Updated:</strong>
                    </p>
                    <p className="text-muted">
                      {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <p>
                      <strong>User Role:</strong>
                    </p>
                    <p className="text-muted">
                      <span
                        className={`badge ${user?.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}
                      >
                        {user?.role || 'User'}
                      </span>
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Blueprint User:</strong>
                    </p>
                    <p className="text-muted">
                      <span className={`badge ${user?.blueprint ? 'bg-success' : 'bg-secondary'}`}>
                        {user?.blueprint ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
